<template>
  <div
    class="control-bar-upper"
    :class="{
      'chart-being-viewed': chartBeingViewed
    }"
  >
    <div class="control-bar-upper-segment control-bar-upper-chart-settings-segment">
      <SymbolInput />
      <div class="control-bar-upper-chart-settings-dropdown-menus">
        <PeriodDropdownMenu />
        <RangeDropdownMenu />
      </div>
    </div>
    <div class="control-bar-upper-segment control-bar-upper-chart-list-ui-segment">
      <div
        class="chart-list-ui interactive-tour-container"
        :class="{
          'no-chart-list-selected': !selectedChartList
        }"
      >
        <div class="chart-list-ui-dropdown-menu-label">
          <label :for="CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID" class="sc3-form-label">
            <span class="phone-hidden">CHARTLIST</span>
            <span class="phone-show"><i class="fas fa-fw fa-user-chart"></i>&nbsp;</span>
          </label>
        </div>
        <div>
          <ChartListDropdownMenu />
        </div>
        <div>
          <ViewAsDropdownMenu
            :chart-list="selectedChartList"
            :selected-chart="selectedChartInSelectedChartList ?? undefined"
            :is-fixed-height="true"
            dropdown-menu-toggle-button-css-color-sc3-button-class="sc3-button-white-and-dark-grey"
            :is-disabled="!selectedChartList && isLoggedIn"
          />
        </div>
        <div class="chart-list-ui-dropdown-menu-label">
          <label :for="CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID" class="sc3-form-label">
            <span class="phone-hidden">CHART</span>
            <span class="phone-show"><i class="custom-scc-icon icon-scc-pos-sq-sharp"></i>&nbsp;</span>
          </label>
        </div>
        <div>
          <ChartsDropdownMenu />
        </div>
        <div class="chart-list-ui-arrow-buttons">
          <button
            type="button"
            title="Previous Chart"
            @click="loadPreviousChart()"
            class="chart-list-ui-arrow-button"
          ><i class="fas fa-fw fa-chevron-circle-left"></i></button>
          <button
            type="button"
            title="Next Chart"
            @click="loadNextChart()"
            class="chart-list-ui-arrow-button"
          ><i class="fas fa-fw fa-chevron-circle-right"></i></button>
        </div>
      </div>
      <div class="chart-crud-ui">
        <div class="interactive-tour-container">
          <button
            type="button"
            title="Save Chart"
            class="sc3-button sc3-button-fixed-height icon-large"
            :class="chartBeingViewed && !doesChartBeingViewedHaveUnsavedChanges ? 'sc3-button-transparent-and-grey' : 'sc3-button-green'"
            @click="launchSaveChartModal()"
            :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
          >
            <i class="fas fa-fw fa-check-circle" v-if="chartBeingViewed && !doesChartBeingViewedHaveUnsavedChanges"></i>
            <i class="fas fa-fw fa-download" v-else></i>
            <span class="medium-desktop-hidden">&nbsp;SAVE</span>
          </button>
          <InteractiveTourWindow
            :page-number="4"
            :position="{ right: (dimensions) => `calc(50% - ${dimensions.baseWidth}px)` /* TODO: just expose the triangle dimensions with export and use here in :style? */ }"
            :arrow-position="InteractiveTourTrianglePosition.TopRight"
          >
            <template v-slot:main-header>
              <span>Save new charts to your account or update existing ones as you work</span>
            </template>
            <template v-slot:explanation>
              <div>
                <p>The green “Save” button above your charts lets you save a new chart to any of the ChartLists in your account.</p>
                <p>If you're working on an existing saved chart and make any changes, you'll notice that the “Save” button turns from grey to green, indicating that changes have been made and need to be re-saved.</p>
                <p>If you want to save those changes as a new chart instead of overwriting the original, you can use the “Save As” button.</p>
              </div>
            </template>
          </InteractiveTourWindow>
        </div>
        <button
          v-if="chartBeingViewed"
          type="button"
          title="Save Chart As"
          class="sc3-button sc3-button-fixed-height sc3-button-transparent-and-light-green icon-large"
          @click="launchSaveChartAsModal()"
          :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
        >
          <i class="fas fa-fw fa-plus-circle"></i>
          <span class="medium-desktop-hidden">&nbsp;SAVE AS</span>
        </button>
        <button
          type="button"
          title="Annotate"
          class="sc3-button sc3-button-fixed-height sc3-button-blue icon-large"
          @click="activateAnnotationMode()"
          :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart || chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value"
        >
          <i class="fas fa-fw fa-pencil-ruler" v-show="!isActivatingAnnotationMode"></i>
          <i class="fad fa-fw fa-spinner-third fa-spin" v-show="isActivatingAnnotationMode"></i>
          <span class="medium-desktop-hidden">&nbsp;Annotate</span>
        </button>
        <div class="interactive-tour-container">
          <button
            type="button"
            title="Inspect (Crosshairs)"
            class="sc3-button sc3-button-fixed-height sc3-button-transparent-and-dark-grey icon-large"
            :class="{ 'sc3-button-active': layoutStore.isInspecting }"
            @click="toggleIsInspecting()"
            :disabled="isInspectorDisabled"
            ref="inspectToggleButtonNode"
          ><i class="fas fa-fw fa-crosshairs"></i></button>
          <InteractiveTourWindow
            :page-number="1"
            :position="{ right: (dimensions) => `calc(50% - ${dimensions.baseWidth}px)` /* TODO: just expose the triangle dimensions with export and use here in :style? */ }"
            :arrow-position="InteractiveTourTrianglePosition.TopRight"
          >
            <template v-slot:main-header>
              <span>Turn on Inspect to see interactive crosshairs and detailed price quotes right on your charts</span>
            </template>
            <template v-slot:explanation>
              <div>
                <p>
                  With the Inspect tool, your cursor allows you to drill deeper into your charts.
                  Vertical and horizontal “crosshairs” let you pinpoint specific areas on the chart and see detailed price quotes for any bar.
                </p>
                <p>
                  You can also click and drag in any direction to measure price and percentage changes or bar counts.
                </p>
              </div>
            </template>
          </InteractiveTourWindow>
        </div>
        <button
          v-if="chartBeingViewed"
          type="button"
          title="Delete Chart"
          class="sc3-button sc3-button-fixed-height sc3-button-red icon-large"
          @click="deleteChart()"
          :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart || isChartBeingViewedReadonly"
        ><i class="fas fa-fw fa-trash-alt"></i></button>
      </div>
    </div>
    <div class="control-bar-upper-segment control-bar-upper-platform-controls-ui-segment">
      <div class="auto-size-checkbox-container">
        <SCThreeCheckbox
          id="control-bar-upper-auto-resize-charts-checkbox"
          :checkbox-value="isAutoSizeChartsEnabled"
          @update:checkbox-value="setIsAutoSizeChartsEnabled"
          label-text="Auto-Size Charts"
          :is-small-font="true"
          :is-square="true"
          :prevent-label-text-from-wrapping-on-whitespace="true"
          :is-disabled="isActivatingAnnotationMode || isAnnotating"
        />
      </div>
      <div class="platform-buttons-container">
        <div class="interactive-tour-container">
          <button
            type="button"
            title="Workbench Preferences"
            class="sc3-button sc3-button-fixed-height sc3-button-grey icon-large"
            @click="launchWorkbenchPreferencesModal()"
          ><i class="fas fa-fw fa-cogs"></i></button>
          <InteractiveTourWindow
            :page-number="2"
            :position="{ right: (dimensions) => `calc(50% - ${dimensions.baseWidth}px)` /* TODO: just expose the triangle dimensions with export and use here in :style? */ }"
            :arrow-position="InteractiveTourTrianglePosition.TopRight"
          >
            <template v-slot:main-header>
              <span>Set workbench preferences like chart alignment</span>
            </template>
            <template v-slot:explanation>
              <div>
                <p>
                  The Workbench Preferences pop-up lets you customize various aspects of your SharpCharts experience.
                  For instance, you can choose to center-align your charts in the middle of the screen or leave them left-aligned for a classic look.
                </p>
              </div>
            </template>
          </InteractiveTourWindow>
        </div>
        <HelpDropdownMenu />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useUnsavedChangesDetection } from '@/composables/useUnsavedChangesDetection';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useAutoRefresh } from '@/composables/useAutoRefresh';
  import { useInspector } from '@/composables/useInspector';
  import { useChartNotesV2 } from '@/composables/useChartNotesV2';
  import { useSymbolInformation } from '@/composables/useSymbolInformation';
  import { InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import { useModals } from '@/composables/useModals';
  import { useChartDimensions } from '@/composables/useChartDimensions';
  import PeriodDropdownMenu from '@/components/dropdowns/PeriodDropdownMenu.vue';
  import ChartsDropdownMenu from '@/components/dropdowns/ChartsDropdownMenu.vue';
  import ChartListDropdownMenu from '@/components/dropdowns/ChartListDropdownMenu.vue';
  import RangeDropdownMenu from '@/components/dropdowns/RangeDropdownMenu.vue';
  import ViewAsDropdownMenu from '@/components/dropdowns/ViewAsDropdownMenu.vue';
  import HelpDropdownMenu from '@/components/dropdowns/HelpDropdownMenu.vue';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import SymbolInput from '@/components/text-inputs/SymbolInput.vue';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
  import { CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID, CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID } from '@/enums/html-ids';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();
  const { doesChartBeingViewedHaveUnsavedChanges } = useUnsavedChangesDetection();
  const {
    loadPreviousChart,
    loadNextChart,
    selectedChartList,
    selectedChartInSelectedChartList,
    chartBeingViewed,
    isChartBeingViewedReadonly
  } = useWorkbenchSelections();
  const { refreshChart } = useAutoRefresh();
  const { isInspectorDisabled, setIsInspecting } = useInspector();
  const { isActivatingAnnotationMode, isAnnotating, activateAnnotationMode } = useChartNotesV2();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();
  const {
    launchCreateAccountModal,
    launchSaveChartModal,
    launchSaveChartAsModal,
    launchWorkbenchPreferencesModal,
    launchDeleteChartModal
  } = useModals();
  const { isAutoSizeChartsEnabled, setIsAutoSizeChartsEnabled } = useChartDimensions();
  const inspectToggleButtonNode = ref<HTMLButtonElement>();

  function deleteChart() {
    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    if (
      !chartBeingViewed.value ||
      isChartBeingViewedReadonly.value
    ) {
      return;
    }

    launchDeleteChartModal(chartBeingViewed.value.recid);
  }

  function toggleIsInspecting() {
    setIsInspecting(!layoutStore.isInspecting);
    refreshChart();
    inspectToggleButtonNode.value?.blur();
  }
</script>

<style scoped>
  .control-bar-upper {
    display: grid;
    align-items: center;
    gap: 4px;
    padding: 4px;
    background-color: #f2f5f7;
    border-bottom: 1px solid #c0ced8;
  }

  .control-bar-upper-segment {
    display: flex;
    align-items: end;
    gap: 4px;
  }

  .control-bar-upper-platform-controls-ui-segment {
    justify-self: end;
    align-items: center;
  }

  .screen-size-phone .control-bar-upper,
  .screen-size-small-tablet .control-bar-upper {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
  }

  .screen-size-phone .control-bar-upper-chart-settings-segment,
  .screen-size-small-tablet .control-bar-upper-chart-settings-segment {
    grid-row: 1;
    grid-column: 1;
    flex-wrap: wrap;
  }

  .screen-size-phone .control-bar-upper-chart-list-ui-segment,
  .screen-size-small-tablet .control-bar-upper-chart-list-ui-segment {
    grid-row: 2;
    grid-column: 1 / 3;
    width: 100%;
    flex-direction: column;
    align-items: center;
  }

  .screen-size-phone .control-bar-upper-platform-controls-ui-segment,
  .screen-size-small-tablet .control-bar-upper-platform-controls-ui-segment {
    grid-row: 1;
    grid-column: 2;
    flex-wrap: wrap-reverse;
    justify-content: end;
  }

  .screen-size-tablet .control-bar-upper,
  .screen-size-extra-small-desktop .control-bar-upper,
  .screen-size-small-desktop .control-bar-upper {
    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
  }

  .screen-size-tablet .control-bar-upper-chart-settings-segment,
  .screen-size-extra-small-desktop .control-bar-upper-chart-settings-segment,
  .screen-size-small-desktop .control-bar-upper-chart-settings-segment {
    grid-row: 1;
    grid-column: 1;
  }

  .screen-size-tablet .control-bar-upper-chart-list-ui-segment,
  .screen-size-extra-small-desktop .control-bar-upper-chart-list-ui-segment,
  .screen-size-small-desktop .control-bar-upper-chart-list-ui-segment {
    grid-row: 2;
    grid-column: 1 / 3;
    width: calc(100% - 32px);
    max-width: 690px;
    justify-self: center;
  }

  .screen-size-tablet .chart-being-viewed .control-bar-upper-chart-list-ui-segment,
  .screen-size-extra-small-desktop .chart-being-viewed .control-bar-upper-chart-list-ui-segment,
  .screen-size-small-desktop .chart-being-viewed .control-bar-upper-chart-list-ui-segment {
    max-width: 750px;
  }

  .screen-size-tablet .control-bar-upper-platform-controls-ui-segment,
  .screen-size-extra-small-desktop .control-bar-upper-platform-controls-ui-segment,
  .screen-size-small-desktop .control-bar-upper-platform-controls-ui-segment {
    grid-row: 1;
    grid-column: 2;
  }

  .screen-size-medium-desktop .control-bar-upper,
  .screen-size-large-desktop .control-bar-upper,
  .screen-size-extra-large-desktop .control-bar-upper {
    grid-template-columns: auto minmax(auto, 780px) 1fr;
    gap: 16px;
  }

  .screen-size-medium-desktop .control-bar-upper.chart-being-viewed,
  .screen-size-large-desktop .control-bar-upper.chart-being-viewed,
  .screen-size-extra-large-desktop .control-bar-upper.chart-being-viewed {
    grid-template-columns: auto minmax(auto, 900px) 1fr;
  }

  .platform-buttons-container {
    display: flex;
    gap: 4px;
  }

  .control-bar-upper-chart-settings-dropdown-menus {
    display: flex;
    gap: 4px;
  }

  .chart-list-ui {
    display: grid;
    width: 100%;
    grid-template-columns: auto 1fr auto;
    gap: 4px;
  }

  .screen-size-medium-desktop .auto-size-checkbox-container,
  .screen-size-large-desktop .auto-size-checkbox-container,
  .screen-size-extra-large-desktop .auto-size-checkbox-container {
    display: none;
  }

  .chart-list-ui > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 0px;
  }

  .chart-list-ui > div.chart-list-ui-dropdown-menu-label {
    justify-content: end;
  }

  .chart-list-ui-dropdown-menu-label label {
    font-size: 10px;
  }

  .chart-list-ui > div.chart-list-ui-arrow-buttons {
    justify-content: space-evenly;
  }

  .chart-list-ui-arrow-button {
    border: none;
    background-color: transparent;
    font-size: 0px;
    color: #707f8f;
    transition: all 0.35s ease;
    touch-action: manipulation;
    user-select: none;
    padding: 0px;
    margin: 0px;
  }

  .chart-list-ui-arrow-button i {
    font-size: 22px;
  }

  .chart-list-ui-arrow-button:hover,
  .chart-list-ui-arrow-button:active,
  .chart-list-ui-arrow-button:focus {
    color: #434c56;
  }

  .chart-crud-ui {
    display: flex;
    gap: 4px;
  }
</style>
